import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { FaCar, FaStar, FaUsers, FaCog } from 'react-icons/fa';

interface WelcomeSlidesProps {
  onComplete: () => void;
}

const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.white};
`;

const SlideContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.xl};
  text-align: center;
`;

const IconContainer = styled.div`
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${theme.spacing.xl};
  color: ${theme.colors.white};
  font-size: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: bold;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.mediumGray};
  line-height: 1.6;
  max-width: 500px;
  margin-bottom: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.lg} ${theme.spacing.xl};
  background: ${theme.colors.lightGray};
`;

const ProgressDots = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  margin-right: ${theme.spacing.lg};
`;

const Dot = styled.div<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.active ? theme.colors.primary : theme.colors.mediumGray};
  transition: all 0.3s ease;
`;

const Button = styled.button`
  background: ${theme.colors.primary};
  color: ${theme.colors.white};
  border: none;
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  border-radius: ${theme.borderRadius.lg};
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${theme.colors.secondary};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const slides = [
  {
    icon: <FaCar />,
    title: "Welcome to 971 Run",
    description: "Your premium car wash experience starts here. We provide top-quality services for your beloved vehicles."
  },
  {
    icon: <FaStar />,
    title: "Premium Services",
    description: "From basic washes to full detailing, we offer a range of services to keep your car looking its best."
  },
  {
    icon: <FaUsers />,
    title: "Community & Events",
    description: "Join our community of car enthusiasts and participate in exciting events and meetups."
  },
  {
    icon: <FaCog />,
    title: "Marketplace",
    description: "Discover trusted car service providers and shops in our comprehensive marketplace."
  }
];

const WelcomeSlides: React.FC<WelcomeSlidesProps> = ({ onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      onComplete();
    }
  };

  const currentSlideData = slides[currentSlide];

  return (
    <Container>
      <SlideContainer>
        <IconContainer>
          {currentSlideData.icon}
        </IconContainer>
        <Title>{currentSlideData.title}</Title>
        <Description>{currentSlideData.description}</Description>
      </SlideContainer>
      
      <ProgressContainer>
        <ProgressDots>
          {slides.map((_, index) => (
            <Dot key={index} active={index === currentSlide} />
          ))}
        </ProgressDots>
        <Button onClick={handleNext}>
          {currentSlide === slides.length - 1 ? 'Get Started' : 'Next'}
        </Button>
      </ProgressContainer>
    </Container>
  );
};

export default WelcomeSlides;


